// 一覧（シェル）の入口：ページの切り替え・言語・下の帯・スマホの下のメニュー。
import "./theme.css";
import "./style.css";
import { nameLater, onSession, startAuth } from "./auth";
import { startEntitlement } from "./entitlement";
import { track } from "./analytics";
import { loadBrandFonts } from "./fonts";
import { getLocale, localeFromPath, localePath, setLocale, t } from "./i18n";
import { h } from "./ui/dom";
import { getFilter, onFilter, setFilter } from "./ui/filter";
import { loadCatalog, type CatalogGame } from "./catalog";
import { gameView } from "./ui/game";
import { mountHeader } from "./ui/header";
import { categories, homeView } from "./ui/home";
import { categoryIcon, icon, iconTile, type IconName } from "./ui/icons";
import { meView } from "./ui/me";
import type { View } from "./ui/view";

const LOCALE_KEY = "appful:locale";
const viewRoot = document.getElementById("view")!;
let current: View | null = null;
let currentRest = "/";

function notFoundView(): View {
  document.title = t("notFound.title");
  return {
    el: h("div", { class: "wrap narrow" }, h("h1", { class: "h1" }, t("notFound.title")), h("a", { class: "btn-sub", href: localePath("/") }, t("notFound.back"))),
    destroy() {},
  };
}

function render() {
  const { locale, rest } = localeFromPath(location.pathname);
  setLocale(locale);
  currentRest = rest;
  current?.destroy();
  document.body.classList.remove("playing");
  const m = /^\/games\/([a-z0-9][a-z0-9-]{1,39})\/?$/.exec(rest);
  current =
    rest === "/" ? homeView()
    : m ? gameView(m[1]!, navigate)
    : /^\/me\/?$/.test(rest) ? meView(switchLocale)
    : notFoundView();
  viewRoot.replaceChildren(current.el);
  renderChrome();
  track("page_view", { page_path: location.pathname, page_title: document.title, language: getLocale() });
}

export function navigate(path: string) {
  if (path === location.pathname) return;
  // 一覧を離れたら、絞り込みを戻す
  if (currentRest === "/") setFilter({ q: "", chip: "all" });
  // サイトの中から移った印。ゲームのページの「もどる」は、これがあれば前のページへ戻り、無ければ（外から直に来たら）ホームへ移る
  history.pushState({ inSite: true }, "", path);
  window.scrollTo(0, 0);
  render();
}

/** ゲームのタイルからゲームのページへ移るときは、押した絵がそのまま次の画面の絵へ大きくなって移る（CrazyGames の移り方を、appful は絵の動きで）。
 *  View Transitions が無いブラウザと、動きを減らす設定の人には、ふつうに切り替える */
function navigateWithMorph(a: HTMLAnchorElement, path: string) {
  const img = a.matches(".gt, .nx-t") ? a.querySelector("img") : null;
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (!img || reduce || !document.startViewTransition || !/\/games\//.test(path)) {
    navigate(path);
    return;
  }
  img.style.viewTransitionName = "game-art";
  // ゲームのページは一覧を読んでから描くので、読み終わるのを待ってから次の画面を写す
  document.startViewTransition(async () => {
    navigate(path);
    await loadCatalog().catch(() => undefined);
    await new Promise((r) => setTimeout(r, 0));
  });
}

function switchLocale() {
  const next = getLocale() === "ja" ? "en" : "ja";
  try {
    localStorage.setItem(LOCALE_KEY, next);
  } catch {
    /* 保存できない環境でも切り替えは効く */
  }
  history.replaceState(null, "", localePath(currentRest, next));
  setLocale(next);
  if (current?.onLocale) {
    current.onLocale();
    renderChrome();
  } else {
    render();
  }
}

/** PC の左のメニュー（CrazyGames の並べ方）：ホーム・続きから・カテゴリー・マイページ。スマホでは出さない（下のメニューを使う） */
let catalogForNav: CatalogGame[] = [];
function renderSideNav() {
  const onHome = currentRest === "/";
  const f = getFilter();
  const go = (chip: string) => () => {
    if (!onHome) navigate(localePath("/"));
    setFilter({ chip, q: "" });
    window.scrollTo(0, 0);
  };
  // どの項目にも、アプリのアイコンの形の絵（うすいピンクの角丸の四角にピンクの線。選んでいる物はピンクの地に白）を付ける
  const item = (label: string, ic: IconName, on: boolean, attrs: Record<string, string | EventListener>) =>
    h(attrs.href ? "a" : "button", { class: on ? "sn on" : "sn", ...(attrs.href ? {} : { type: "button" }), ...attrs }, iconTile(ic), h("span", {}, label));
  document.getElementById("sidenav")!.replaceChildren(
    item(t("nav.home"), "home", onHome && f.chip === "all" && !f.q, { onclick: go("all") }),
    item(t("nav.continue"), "resume", false, { href: `${localePath("/")}#continue` }),
    h("p", { class: "sn-h" }, t("nav.categories")),
    ...categories(catalogForNav)
      .filter((c) => c.id !== "all")
      .map((c) => item(c.label, categoryIcon(c.id), onHome && f.chip === c.id, { onclick: go(c.id) })),
    h("hr", { class: "sn-hr" }),
    item(t("nav.me"), "user", /^\/me\/?$/.test(currentRest), { href: localePath("/me/") }),
  );
}
onFilter(renderSideNav);
loadCatalog()
  .then((list) => {
    catalogForNav = list;
    renderSideNav();
  })
  .catch(() => undefined);

/** 下の帯とスマホの下のメニュー。言語とページが変わるたびに作り直す */
function renderChrome() {
  renderSideNav();
  document.querySelector('link[rel="canonical"]')?.setAttribute("href", location.origin + location.pathname);
  document.getElementById("footer")!.replaceChildren(
    h(
      "div",
      { class: "ft-in" },
      h(
        "div",
        { class: "ft-top" },
        // 会社の LP のフッターと同じ並び：ふくらんだロゴ｜合言葉、右に案内
        h(
          "div",
          { class: "ft-brand" },
          h("a", { class: "ft-logo", href: localePath("/"), "aria-label": "appful games" }, h("span", {}, "appful"), h("span", {}, "games")),
          h("span", { class: "ft-tag" }, t("footer.tag")),
        ),
        h(
          "div",
          { class: "ft-links" },
          // 案内は静的な日本語ページへ、ホームは表示中の言語へ移る
          h("a", { href: localePath("/") }, t("nav.home")),
          h("a", { href: "/about/", target: "_self" }, t("footer.about")),
          h("a", { href: "/legal/privacy/", target: "_self" }, t("footer.privacy")),
          h("a", { href: "/legal/contact/", target: "_self" }, t("footer.contact")),
          h("a", { href: "/legal/tokushoho/", target: "_self" }, t("footer.tokushoho")),
          h("button", { type: "button", onclick: switchLocale }, "日本語 / English"),
        ),
      ),
      h("div", { class: "ft-bottom" }, h("p", { class: "ft-note ph" }, t("footer.note")), h("p", { class: "ft-co" }, t("footer.co"))),
    ),
  );
  const tab = (href: string, name: "home" | "search" | "resume" | "user", label: string, on: boolean) =>
    h("a", { href, class: on ? "on" : "" }, icon(name), h("span", {}, label));
  document.getElementById("tabbar")!.replaceChildren(
    tab(localePath("/"), "home", t("nav.home"), currentRest === "/"),
    tab(`${localePath("/")}#search`, "search", t("nav.search"), false),
    tab(`${localePath("/")}#continue`, "resume", t("nav.continue"), false),
    tab(localePath("/me/"), "user", t("nav.me"), /^\/me\/?$/.test(currentRest)),
  );
}

window.addEventListener("popstate", render);
// サイトの中のリンクは、ページを読み直さずに移る
document.addEventListener("click", (ev) => {
  const a = (ev.target as HTMLElement).closest?.("a[href^='/']") as HTMLAnchorElement | null;
  if (!a || ev.defaultPrevented || ev.metaKey || ev.ctrlKey || ev.shiftKey || a.target) return;
  if (a.pathname.endsWith(".json") || a.pathname.startsWith("/thumbs/") || a.pathname.startsWith("/legal/") || a.pathname.startsWith("/about/")) return;
  ev.preventDefault();
  // 「さがす」は上の帯の検索の箱を開く（押した手の中で focus しないと iPhone はキーボードを出さない）
  if (a.hash === "#search") document.querySelector<HTMLInputElement>('[data-testid="search"]')?.focus();
  if (a.pathname !== location.pathname) navigateWithMorph(a, a.pathname);
  // 「続きから」は、一覧のその場所へ動かす
  if (a.hash && a.hash !== "#search") requestAnimationFrame(() => document.getElementById(a.hash.slice(1))?.scrollIntoView({ block: "start" }));
});

// 前に英語を選んだ人が日本語のトップに来たら、英語のトップへ移す（検索のロボットは記録を持たないので影響しない）。
try {
  if (location.pathname === "/" && localStorage.getItem(LOCALE_KEY) === "en") history.replaceState(null, "", "/en/");
} catch {
  /* 読めない環境では何もしない */
}

/**
 * ログインのあと、名前をまだ決めていない人に「ランキングに出る名前を決める」窓を出す（ui/name-dialog.ts）。
 * 今の名前の人（2026-10-25 より前にログインした人）にも、次に開いたときに 1 回出す。
 * 「あとで決める」を押した人には、ログインし直すまで出さない（auth.ts の rememberNameLater）。1 回開いたページでは 1 回だけ。
 * プロフィールを読めなかったとき（nameChosen が null）は出さない
 */
function startNamePrompt() {
  const shown = new Set<string>();
  onSession((s) => {
    if (!s.ready || !s.user || s.nameChosen !== false) return;
    const uid = s.user.uid;
    if (shown.has(uid) || nameLater(uid)) return;
    shown.add(uid);
    void import("./ui/name-dialog").then((m) => m.openNameDialog({ from: "login" }));
  });
}

startNamePrompt();
startAuth();
startEntitlement();
loadBrandFonts();
mountHeader(document.getElementById("header")!, navigate, switchLocale);
render();

